/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/

/* eslint-disable react/destructuring-assignment */
/* eslint-disable react/state-in-constructor */
import { Component, ReactNode } from "react"
import { connect } from "react-redux"
import { Dispatch } from "redux"
import { actionCreator } from "store/store"

/**
 * Intentionally NOT a `MonitorErrorBoundary` (see `@grab-food/web-monitoring-helpers`
 * and `src/containers/NavigationContainer.tsx`'s page-level boundary), and
 * intentionally kept below page level even though the BTP Web Frontend
 * Monitoring Standard says not to add boundaries there. Reason: granularity.
 * `LazyLoadWrapper` (this boundary's only consumer) wraps individual lazy
 * chunks nested deep inside already-rendered pages — a breadcrumb, a banner,
 * one tab of a list (e.g. `OrderList`'s per-tab `<LazyLoadWrapper>`s) — not
 * just top-level routes. Lifting this to the page boundary would replace the
 * *entire* page with the crash fallback for a failure in one small nested
 * widget, instead of just prompting a reload for that widget while the rest
 * of the page keeps working.
 *
 * This boundary only owns the `ChunkLoadError` case (stale post-deploy
 * chunk → reload modal). Everything else is re-thrown from
 * `getDerivedStateFromError` before it commits, so React keeps walking up
 * looking for the next boundary — the page-level `MonitorErrorBoundary` in
 * `NavigationContainer` — which is the one that actually reports to Datadog.
 * Swallowing every error here (the old behavior) meant genuine bugs inside
 * any `LazyLoadWrapper` were invisible outside a `console.error`.
 */

interface Props {
  children?: ReactNode
  dispatch: Dispatch
}

interface State {
  hasError: boolean
}

const isChunkLoadError = (error: Error): boolean =>
  error?.name === "ChunkLoadError"

class ChunkErrorBoundary extends Component<Props, State> {
  state: State = {
    hasError: false,
  }

  static getDerivedStateFromError(error: Error) {
    if (!isChunkLoadError(error)) {
      // Not our concern — hand it to the next ancestor boundary instead of
      // swallowing it here.
      throw error
    }
    return { hasError: true }
  }

  componentDidCatch() {
    // Only reached for `ChunkLoadError`: `getDerivedStateFromError` above
    // re-throws anything else before this boundary's state (and therefore
    // this callback) ever commits.
    const { dispatch } = this.props
    dispatch(actionCreator.global.toggleChunkErrorModal())
  }

  render() {
    if (this.state.hasError) {
      return null
    }

    return this.props.children
  }
}

export default connect()(ChunkErrorBoundary)
